import { environment } from '../../../environments/environment';

/**
 * Picks the backend for a main-API call: prism-new when the call's method + path is listed in
 * `environment.prismNew.endpoints`, otherwise the old API (`environment.apiUrl`).
 */

interface CompiledEndpoint {
  method: string;
  /** Literal path segments; '' stands for a `{param}` that matches any one segment. */
  segments: string[];
  /** The pattern ended in `{*}`, so it also matches every deeper path. */
  matchesRest: boolean;
}

const PARAM_SEGMENT = /^\{[^}]*\}$/;

function pathSegments(url: string): string[] {
  return url.split(/[?#]/)[0].split('/').filter(Boolean);
}

export function compileEndpoints(endpoints: readonly string[]): CompiledEndpoint[] {
  return endpoints.map((entry) => {
    const [method, path = ''] = entry.trim().split(/\s+/);
    const segments = pathSegments(path);
    const matchesRest = segments[segments.length - 1] === '{*}';
    return {
      method: method.toUpperCase(),
      segments: (matchesRest ? segments.slice(0, -1) : segments).map((s) => (PARAM_SEGMENT.test(s) ? '' : s)),
      matchesRest,
    };
  });
}

export function matchesEndpoint(method: string, url: string, endpoints: CompiledEndpoint[]): boolean {
  const upperMethod = method.toUpperCase();
  const segments = pathSegments(url);
  return endpoints.some((endpoint) => {
    if (endpoint.method !== upperMethod) return false;
    const lengthFits = endpoint.matchesRest
      ? segments.length > endpoint.segments.length
      : segments.length === endpoint.segments.length;
    return lengthFits && endpoint.segments.every((s, i) => s === '' || s === segments[i]);
  });
}

/** Lookup over a `{ 'METHOD /path/{param}': value }` table, using the same matching as routing. */
export function endpointTable<T>(table: Record<string, T>): (method: string, url: string) => T | undefined {
  const entries = Object.entries(table).map(([pattern, value]) => ({ pattern: compileEndpoints([pattern]), value }));
  return (method, url) => entries.find((entry) => matchesEndpoint(method, url, entry.pattern))?.value;
}

let compiledEndpoints: CompiledEndpoint[] | null = null;

/** Base URL for a main-API call; `url` is the path under the API root, e.g. `call-audit/reports`. */
export function apiBaseUrlFor(method: string, url: string): string {
  const { prismNew } = environment;
  if (!prismNew.enabled || /^https?:\/\//i.test(url)) return environment.apiUrl;
  compiledEndpoints ??= compileEndpoints(prismNew.endpoints);
  return matchesEndpoint(method, url, compiledEndpoints) ? prismNew.apiUrl : environment.apiUrl;
}

/** Full URL for a main-API call made outside ApiService (fetch, sendBeacon). */
export function apiUrlFor(method: string, path: string): string {
  return `${apiBaseUrlFor(method, path).replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}
